import {
  getCompletionTxInfo,
  resolveOnChainCompletionTxHash,
  useDirectExecutionForDepositAddress,
} from '@funkit/connect-core'
import { useEffect, useState } from 'react'
import type { Address } from 'viem'

import {
  UNSET_WALLET_ADDRESS,
  useFunkitIdentity,
} from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import {
  type WithdrawalStatusResult,
  useWithdrawalStatus,
} from './useWithdrawalStatus'

/**
 * Slow by default because a venue settles in minutes, not seconds — a faster
 * cadence only burns polls on a record the backend has not seen yet. Callers
 * that know their venue's cadence can say so.
 */
const DEFAULT_POLL_INTERVAL_MS = 10_000

export interface DepositAddressWithdrawalStatus extends WithdrawalStatusResult {
  /**
   * Tx hash of the transaction that delivered the funds on the destination chain.
   * Undefined cross-chain until the bridge fills and reports that transaction.
   */
  txHash: string | undefined
}

/**
 * Follows a withdrawal a venue submitted for us, which left a deposit address
 * rather than a transaction of ours.
 *
 * Two stages: find the direct execution that records the arrival at that
 * address, then follow it with {@link useWithdrawalStatus} exactly as the relay
 * rail does — so both rails agree on what a status means. Stage one is shared
 * with web, including the guard against an earlier withdrawal that reused the
 * same address reading as an instant success.
 */
export function useDepositAddressWithdrawalStatus(
  depositAddress: Address | undefined,
  pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
): DepositAddressWithdrawalStatus {
  const { apiKey, walletAddress } = useFunkitIdentity()

  // The finder takes a flat interval and would keep asking about a finished
  // withdrawal, so the outcome is latched for the next render.
  const [hasSettled, setHasSettled] = useState(false)

  const { latestStep, isFinalStep } = useDirectExecutionForDepositAddress({
    depositAddress,
    apiKey,
    // The wallet, not the host's `userId`: the deposit address is registered
    // under the owner wallet, and the scanner records the arrival there.
    // The unset sentinel is truthy, so the finder needs it mapped away or it
    // polls for a user that does not exist.
    walletAddress: walletAddress === UNSET_WALLET_ADDRESS ? '' : walletAddress,
    pollIntervalMs,
    hasSettled,
    logger,
  })

  const arrivalStatus = useWithdrawalStatus(latestStep?.txHash)
  // SUCCESS on an intermediary step is not delivery — the funds are still
  // bridging, and saying otherwise would land the screen early.
  const isCompleted = arrivalStatus.isCompleted && isFinalStep
  const completionTxHash = latestStep
    ? resolveOnChainCompletionTxHash(
        latestStep,
        getCompletionTxInfo(latestStep).txHash,
      )
    : undefined

  useEffect(() => {
    if (isCompleted || arrivalStatus.isFailed) {
      setHasSettled(true)
    }
  }, [isCompleted, arrivalStatus.isFailed])

  return { ...arrivalStatus, isCompleted, txHash: completionTxHash }
}
